'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { missionDetailHref } from '../missions/_components/missionListShared';
import type { CopRotationSubmitting } from './useCopRotationControls';

/**
 * SUSPEND, RESUME AND DETACH - BEHIND A SECOND PRESS, AND NEVER BESIDE APPROVE.
 *
 * `CoverageActionCard`'s own header is the argument, and it is worth quoting because this component
 * exists to keep obeying it on a smaller surface:
 *
 *   "Eight buttons used to sit in a wrap under the forecast [...] most of them permanently
 *    disabled, most of the time. A row of controls that is usually inert trains an operator to
 *    skip it, which is the worst possible property for the row that also carries 'approve this
 *    launch'. [...] Suspend, Resume and Detach are not here - they are deliberate interventions in
 *    the capability's life, not responses to this cycle, and they do not belong in the same row as
 *    Approve."
 *
 * A DISCLOSURE RATHER THAN A POPUP MENU because this codebase has no menu primitive
 * (`components/ui/` has button, dialog, input, label, select, switch, table, textarea) and a
 * hand-rolled one would owe focus trapping, escape handling, outside-click dismissal and
 * positioning inside a scrolling panel - a lot of new a11y surface for a control an operator
 * touches a handful of times a day.
 *
 * FORCE DETACH IS ABSENT. Admin-only, bypasses the airborne gate, and it is the escape hatch for a
 * stuck row rather than an operating control. When the ordinary detach is refused because an
 * aircraft is up, this points at the mission page instead of quietly offering the override here.
 *
 * EVERY CONSEQUENCE SENTENCE HERE IS THE MISSION PANEL'S, and that is not laziness. Review found
 * this file had written fresh copy that dropped two effects the panel states explicitly - that
 * suspending DISCARDS the standing replacement, approval included, and that detaching COMPLETES a
 * mission still running. Both are true on the server. Two surfaces telling an operator different
 * things about one irreversible action is its own defect, and the pre-press sentence is the only
 * one that can change a decision: an operator presses before they can see the aftermath.
 */

interface CopCoverageLifecycleProps {
  missionId: string;
  /** `suspended` from the coverage row - which of Suspend and Resume is the live one. */
  suspended: boolean;
  /** Terminal. Suspend is refused by the server, so it is not offered. */
  stopped: boolean;
  /** A rotation of one (#564), whose suspend consequence is materially different. */
  selfSwapRotation: boolean;
  /**
   * The aircraft holding the station right now, when the coverage row says one is, else null.
   *
   * Detach is refused for as long as any aircraft on the rotation has an open flight row, which on
   * a `covered` or `handover` station is the normal case rather than the exceptional one. Saying so
   * before the press is the whole of this prop's job.
   */
  stationHeldBy: string | null;
  /** Is the mission still running? Decides whether detaching also COMPLETES it. */
  missionRunning: boolean;
  submitting: CopRotationSubmitting;
  busy: boolean;
  /**
   * Armed state lives in the parent, so arming this cannot coexist with an armed ROTATE NOW.
   * Two live destructive confirms 60 px apart in a 288 px column is a defect the mission panel
   * found and fixed once already.
   */
  detachArmed: boolean;
  onArmDetach: () => void;
  onDisarm: () => void;
  /** The server's refusal when an aircraft is airborne, or null. */
  airborneRefusal: string | null;
  onDismissRefusal: () => void;
  onSuspend: () => void;
  onResume: () => void;
  onDetach: () => void;
}

export function CopCoverageLifecycle({
  missionId, suspended, stopped, selfSwapRotation, stationHeldBy, missionRunning, submitting, busy,
  detachArmed, onArmDetach, onDisarm, airborneRefusal,
  onDismissRefusal, onSuspend, onResume, onDetach,
}: CopCoverageLifecycleProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const cautionId = `${panelId}-detach-caution`;
  const refusalRef = useRef<HTMLDivElement | null>(null);
  /** This whole strip, so the effect below can tell "focus is still in here" from "focus moved on". */
  const panelRef = useRef<HTMLDivElement | null>(null);
  /** The Detach button, so dismissing the refusal returns focus to the control it was about. */
  const detachRef = useRef<HTMLButtonElement | null>(null);

  /**
   * THE REFUSAL COMES TO THE OPERATOR, rather than waiting to be scrolled to.
   *
   * `role="alert"` is not enough on its own here. This box renders at the bottom of a selected
   * mission row that is now roughly 400 px tall, inside a scrolling queue - so on the press that
   * produces it, it is very often below the viewport. An operator pressed Confirm detach, read a
   * button whose label had not changed (that is fixed separately, by disarming on settle), and
   * pressed it again: two DELETEs three seconds apart, both refused, neither ever seen.
   *
   * Focus as well as scroll, because scrolling serves only the operator who is looking. The
   * keyboard operator's focus is otherwise still on a control that says nothing about what
   * happened.
   */
  useEffect(() => {
    if (!airborneRefusal) return;
    const el = refusalRef.current;
    if (!el) return;
    // jsdom implements neither, and a test environment must not be the thing that decides whether
    // a safety refusal renders at all.
    //
    // SCROLLING IS UNCONDITIONAL, TAKING FOCUS IS NOT.
    //
    // A detach is bounded at 10 s and the Timeline button is not gated on `busy`, so an operator
    // can press Confirm detach and open the timeline dialog before the answer lands. Radix traps
    // focus in that dialog and marks the rest of the page `aria-hidden`; grabbing focus into a
    // hidden region gets it yanked straight back, and the refusal is then neither seen NOR
    // announced - losing the one delivery guarantee this effect exists for. The same unconditional
    // grab would also interrupt someone who had moved to a text field in that window.
    //
    // So: take focus only when it is somewhere this can safely be taken from - nowhere in
    // particular, or inside this strip - and never into a hidden subtree.
    if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
    if (typeof el.focus !== 'function') return;
    if (el.closest('[aria-hidden="true"]')) return;
    const active = document.activeElement;
    const focusIsParked = active === null || active === document.body || el.contains(active)
      || panelRef.current?.contains(active) === true;
    if (focusIsParked) el.focus();
  }, [airborneRefusal]);

  /** Closing the strip disarms. An armed destructive control must never survive out of sight. */
  const toggle = () => {
    setOpen((wasOpen) => {
      if (wasOpen) onDisarm();
      return !wasOpen;
    });
  };

  /**
   * `aria-disabled` and a guarded handler, NOT `disabled`.
   *
   * The rule is stated on `CoverageActionCard` and applies to every control in this feature: "a
   * focused button that becomes `disabled` mid-press drops focus to `<body>` in Chromium, and
   * `disabled` also silences `aria-busy`." A keyboard operator pressing Suspend was losing their
   * place in the panel.
   */
  const guard = (fn: () => void) => () => { if (!busy) fn(); };

  return (
    <div className="mt-1.5" ref={panelRef}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-6 px-1.5 text-[9.5px] uppercase tracking-wider text-muted-foreground"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={panelId}
      >
        {open
          ? <ChevronDown className="h-3 w-3 mr-1" aria-hidden />
          : <ChevronRight className="h-3 w-3 mr-1" aria-hidden />}
        More rotation controls
      </Button>

      {open && (
        <div id={panelId} className="mt-1 pl-1 space-y-1.5">
          {/* SUSPEND AND RESUME ARE ONE SLOT, not two controls one of which is always inert - the
              rotation is either running or it is not, and the strip says which by offering the
              move that is available. Neither is offered on a STOPPED rotation: the server refuses
              both, and a control that is guaranteed to error reads as if it will do something. */}
          {!stopped && (
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(
                  'h-6 px-2 text-[10px] uppercase tracking-wider w-full justify-start',
                  busy && 'opacity-50',
                )}
                aria-disabled={busy}
                aria-busy={submitting === 'suspend' || submitting === 'resume'}
                onClick={guard(suspended ? onResume : onSuspend)}
              >
                {submitting === 'suspend' ? 'Suspending...'
                  : submitting === 'resume' ? 'Resuming...'
                    : suspended ? 'Resume rotation' : 'Suspend rotation'}
              </Button>
              <p className="text-[10px] text-muted-foreground leading-snug mt-0.5">
                {suspended
                  // The panel's sentence: an approval does NOT survive a suspend, so the operator
                  // has to know a fresh decision is coming rather than assuming theirs still holds.
                  ? 'Resuming re-evaluates the station from scratch - the next replacement is'
                    + ' planned fresh, and needs approving again.'
                  : selfSwapRotation
                    // #564's case, and the reason the panel puts it on the CONTROL: this rotation
                    // has no relief, so suspending mid-cycle strands the one aircraft on the ground.
                    ? 'Suspending stops replacements being scheduled and discards the one standing,'
                      + ' approval included. This rotation has ONE aircraft: if it is already flying'
                      + ' its swap cycle, suspending ends that cycle at the dock and it will not be'
                      + ' sent back up until you resume.'
                    : 'Suspending stops replacements being scheduled and discards the one standing,'
                      + ' approval included. It does not recall anything: an aircraft already in the'
                      + ' air keeps flying its last command.'}
              </p>
            </div>
          )}

          {/*
            ONE ELEMENT, LABEL SWAPPED - not two elements swapped for each other.

            Replacing the button with a different subtree on arm dropped a keyboard operator's
            focus to `<body>`, with no announcement that a confirm had appeared and no way to find
            it again. The mission page keeps one button and changes its variant and its words, and
            so does this. It also makes the in-flight label reachable: the previous version cleared
            the arm in the same click that started the request, so "Detaching..." could never
            render and the operator got a greyed button and no sign anything was happening on the
            one irreversible control here.
          */}
          <div>
            <Button
              type="button"
              variant={detachArmed ? 'destructive' : 'outline'}
              size="sm"
              className={cn(
                'h-6 px-2 text-[10px] uppercase tracking-wider w-full justify-start',
                !detachArmed && 'text-status-red-fg border-status-red-marker',
                busy && 'opacity-50',
              )}
              aria-disabled={busy}
              aria-busy={submitting === 'detach'}
              // The prediction below is about THIS control, so a screen reader has to get it with
              // the label rather than only on sighted arrival - every other consequence sentence in
              // this feature is wired the same way.
              aria-describedby={stationHeldBy !== null ? cautionId : undefined}
              ref={detachRef}
              onClick={guard(() => { if (detachArmed) onDetach(); else onArmDetach(); })}
            >
              {submitting === 'detach' ? 'Detaching...'
                : detachArmed ? 'Confirm detach' : 'Detach coverage'}
            </Button>
            {/*
              THE REFUSAL, PREDICTED, BEFORE THE PRESS RATHER THAN AFTER IT.

              `detachCoverage` refuses while any aircraft on the rotation has an open flight row,
              and on a `covered` or `handover` station that is the ORDINARY case, not the edge one -
              an aircraft holding the station is the capability working. So the first cut armed a
              two-press destructive confirm for a request it had the information to know would be
              refused, and only said why afterwards, in a box below the fold.

              THE BUTTON STAYS LIVE. The server's gate is org-wide over open flight rows, which is
              strictly broader than what this block can see: the aircraft may have landed since the
              last poll, or a member not on station may be flying something else entirely. A
              prediction may inform a press; it must never block one the server would have allowed.
              That is the same rule this feature applies to a null `capableTypes`, and the reason
              the Timeline button above is enabled with its explanation on screen rather than
              disabled with its reason in a `title`.
            */}
            {/* AND NOT WHILE THE SERVER HAS ACTUALLY REFUSED. Once the refusal below is on screen
                it is the authoritative version of this same sentence, and it carries the same
                override link - leaving the prediction up alongside it says one thing twice, in two
                boxes, with two routes to the same page. The refusal wins. */}
            {stationHeldBy !== null && !detachArmed && submitting !== 'detach'
              && airborneRefusal === null && (
              <p id={cautionId} className="text-[10px] leading-snug text-muted-foreground mt-0.5">
                {stationHeldBy} is on station. Detaching is refused while an aircraft on this
                rotation has a flight in progress.{' '}
                {/* THE WAY THROUGH, ON THE WARNING RATHER THAN ONLY ON THE REFUSAL. The override
                    used to appear only inside the refusal box - which is to say, only AFTER the
                    pointless press this sentence exists to prevent. */}
                <Link
                  href={missionDetailHref(missionId)}
                  className="underline underline-offset-2 whitespace-nowrap"
                >
                  Override on the mission page
                </Link>
              </p>
            )}
            {detachArmed && submitting !== 'detach' && (
              <div className="mt-1 rounded-sm border border-status-red-marker p-1.5">
                {/* THE CONSEQUENCES, BEFORE THE CONFIRM, and BOTH of them. Detach removes the
                    capability and does not recall anything - and it also completes a mission that
                    is still running, which on an in-progress sortie is the difference between
                    standing a station down and closing out a live flight. The first cut said only
                    the first half, and the operator learned the second from a toast after the
                    irreversible press. */}
                <p className="text-[10px] leading-snug mb-1.5">
                  Stand the station down? Coverage stops being held, and an aircraft on station is{' '}
                  <strong>not</strong> recalled by this.
                  {missionRunning && ' A mission still running is completed.'}
                </p>
                {/*
                  THE PREDICTION SURVIVES THE ARM.

                  The copy above describes a detach that SUCCEEDS. Gating the caution on
                  `!detachArmed` meant arming replaced "this will be refused" with "here is what it
                  will do" - so at the moment of the irreversible confirm, the surface asserted the
                  opposite of what it had said one press earlier, and the box designed to be read at
                  the confirm was the one that was wrong. An operator reading only the red box walks
                  into the 409 believing the opposite.
                */}
                {stationHeldBy !== null && (
                  <p className="text-[10px] leading-snug mb-1.5 text-status-amber-fg">
                    {stationHeldBy} is still flying, so this will most likely be refused.
                  </p>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[10px] uppercase tracking-wider"
                  onClick={onDisarm}
                >
                  Keep coverage
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/*
        OUTSIDE THE DISCLOSURE, and that is the point.
        This is a safety refusal about a press that just happened - an aircraft is airborne on the
        rotation the operator asked to stand down. Rendering it inside the strip would let it be
        hidden by collapsing the very section it belongs to, one press after the refusal, which is
        the state an operator is most likely to leave the panel in. It stays until dismissed.
      */}
      {airborneRefusal && (
        <div
          ref={refusalRef}
          tabIndex={-1}
          className={cn(
            'mt-1.5 rounded-sm border border-status-amber-marker p-1.5',
            'text-[10px] leading-snug text-status-amber-fg',
            // DRAWN WHEN IT TAKES FOCUS. `globals.css` sets only `outline-ring/50` - an outline
            // COLOUR with no width - so a programmatically focused container gets nothing at all,
            // and a mouse operator's next Tab starts from a place they were never shown.
            // `focus:` rather than `focus-visible:`, because a pointer-initiated programmatic focus
            // does not match `:focus-visible`. `CoverageActionCard` documents and applies exactly
            // this workaround for the same reason.
            'focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-foreground',
          )}
          role="alert"
        >
          <p className="flex items-start gap-1 mb-1">
            <AlertTriangle className="h-3 w-3 mt-px shrink-0" aria-hidden />
            <span>{airborneRefusal}</span>
          </p>
          {/*
            THE OVERRIDE IS NAMED, NOT OFFERED. Force detach is admin-only and skips the gate that
            just refused; a link makes the operator arrive at the panel that explains it, rather
            than putting a safety bypass one press from a refusal on a 320 px strip.

            The two targets are on their own lines rather than sharing one: at 288 px they were
            adjacent 10 px inline targets, and mis-tapping Dismiss loses the only route to the
            override.
          */}
          <Link
            href={missionDetailHref(missionId)}
            className="block py-1 underline underline-offset-2 uppercase tracking-wider"
          >
            Open the mission page to override
            <ExternalLink className="h-3 w-3 inline ml-1" aria-hidden />
          </Link>
          <button
            type="button"
            // Dismissing unmounts the element focus is sitting on, so without this a keyboard
            // operator lands on `<body>` and has to tab back through the whole block. Focus goes to
            // the control the refusal was about.
            onClick={() => { onDismissRefusal(); detachRef.current?.focus(); }}
            className="block py-1 underline underline-offset-2 uppercase tracking-wider cursor-pointer"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
